import axios from 'axios';
import isArray from 'lodash/isArray';
import reduce from 'lodash/reduce';
import { sanitizeEntity } from './sanitize';
import { BUSINESS_LISTING_TYPE_PUBLISHED } from './types';
import { createSlug, eventDetailsUrl } from './urlHelpers';
import moment from 'moment';
import { boothTypes, EventType } from '../config/configListing';

import { types as sdkTypes } from './sdkLoader';

const { LatLng } = sdkTypes;

const REACT_APP_MARKETPLACE_ROOT_URL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
const dev = ['https://wle-dev-14ed7c8bb1b4.herokuapp.com', 'http://localhost:3000'].includes(REACT_APP_MARKETPLACE_ROOT_URL);

// For dev 5 mins and 10 mins as per QA adn Darren
export const mailTime = {
  fourHours: dev ? 5 : (4 * 60),
  fortyEightHours: dev ? 10 : (48 * 60),
  sixHours: dev ? 6 : (6 * 60),
  twelveHours: dev ? 12 : (12 * 60),
}

export const templateIds = {
  RFP_NEW_MATCH_TEMPLATE_ID: "d-e703e957a4af4cc0957e397e3eb21bc6",
  RFP_TO_ADMIN_TEMPLATE_ID: "d-1cc2c550c65f4f78a705c2a8c176e58c",
  RFP_PUBLISHED_TO_ADMIN_TEMPLATE_ID: "d-72e6c5aa2c1e40d49bf6255c4d8fb7ae",
  SIGNUP_DRAFT_TEMPLATE_ID: "d-8b30f930132f4034bbce40c256da8602",
  SIGNUP_FOLLOWUP_6H_TEMPLATE_ID: "d-15ab5c7998b049df8566830382be3202",
  SIGNUP_FOLLOWUP_12H_TEMPLATE_ID: "d-1af84d265d594d11afd38d4a5a9c4f3d",
  BUSINESS_DRAFT_TEMPLATE_ID: "d-361cef19b6e54a369af82e3a5514ca1f",
  EVENT_DRAFT_TEMPLATE_ID: "d-ff533a76d3ec43ccb39695d896770608",
  ENAGAGE_INCOMPLETE_SIGNUP_TEMPLATE_ID: "d-192734136bce4b8daead0201d9b267cc",
  ENAGAGE_INCOMPLETE_BUSSINESS_TEMPLATE_ID: "d-1745093dedf348baa1c1ab144ebab359",
  ENAGAGE_INCOMPLETE_EVENT_TEMPLATE_ID: "d-7fd00bfcf37a48e580072a966f38977c",
  UPCOMING_EXPERT_EVENT_TEMPLATE_ID: "d-f4cdaf47fcb04373bcf282e26ac121bc",
  UPCOMING_OWNER_EVENT_TEMPLATE_ID: "d-98d0a5a7493a4392b4c06846c1d72e48",
  SEVEN_DAYS_BEFORE_COMMISION_EXPIRE_TEMPLATE_ID: "d-c7d3b0e3536c435398b118b0d9221411",
  ADMIN_REVIEW_TEMPLATE_ID: "d-d2ec02750fa64c338e74cd744df7f3dd",
  ADMIN_NO_RFP_MATCH_TEMPLATE_ID: "d-1cc2c550c65f4f78a705c2a8c176e58c",
  ADMIN_RFP_BID_EXPIRED_TEMPLATE_ID: "d-31ee082c08e149908a7e652860deb43f",
  RFP_PAYOUT_TEMPLATE_ID: "d-9ca74840f63247ea86a31b9d2cef3254",
  RFP_REVIEW_REMEMBER_TEMPLATE_ID: "d-c0cdf0c9d7c2412aa8d26d7a2f4239b2",
  RFP_BID_UPDATED_CUSTOMER_TEMPLATE_ID: "d-7938e92e29354b898b1c6a528c088668",
  RFP_BID_UPDATED_PROVIDER_TEMPLATE_ID: "d-08d490980e1e43c6973b0d701f64005c",
  TRADE_SIGNUP_DRAFT_TEMPLATE_ID: "d-349d899dbc3b40459f86166deda33afc",
};

// NOTE: This file imports sanitize.js, which may lead to circular dependency

/**
 * Combine the given relationships objects
 *
 * See: http://jsonapi.org/format/#document-resource-object-relationships
 */
export const combinedRelationships = (oldRels, newRels) => {
  if (!oldRels && !newRels) {
    // Special case to avoid adding an empty relationships object when
    // none of the resource objects had any relationships.
    return null;
  }
  return { ...oldRels, ...newRels };
};

/**
 * Combine the given resource objects
 *
 * See: http://jsonapi.org/format/#document-resource-objects
 */
export const combinedResourceObjects = (oldRes, newRes) => {
  const { id, type } = oldRes;
  if (newRes.id.uuid !== id.uuid || newRes.type !== type) {
    throw new Error('Cannot merge resource objects with different ids or types');
  }
  const attributes = newRes.attributes || oldRes.attributes;
  const attributesOld = oldRes.attributes || {};
  const attributesNew = newRes.attributes || {};
  // Allow (potentially) sparse attributes to update only relevant fields
  const attrs = attributes ? { attributes: { ...attributesOld, ...attributesNew } } : null;
  const relationships = combinedRelationships(oldRes.relationships, newRes.relationships);
  const rels = relationships ? { relationships } : null;
  return { id, type, ...attrs, ...rels };
};

/**
 * Combine the resource objects form the given api response to the
 * existing entities.
 */
export const updatedEntities = (oldEntities, apiResponse, sanitizeConfig = {}) => {
  const { data, included = [] } = apiResponse;
  const objects = (Array.isArray(data) ? data : [data]).concat(included);

  const newEntities = objects.reduce((entities, curr) => {
    const { id, type } = curr;

    // Some entities (e.g. listing and user) might include extended data,
    // you should check if src/util/sanitize.js needs to be updated.
    const current = sanitizeEntity(curr, sanitizeConfig);

    entities[type] = entities[type] || {};
    const entity = entities[type][id.uuid];
    entities[type][id.uuid] = entity ? combinedResourceObjects({ ...entity }, current) : current;

    return entities;
  }, oldEntities);

  return newEntities;
};

/**
 * Denormalise the entities with the resources from the entities object
 *
 * This function calculates the dernormalised tree structure from the
 * normalised entities object with all the relationships joined in.
 *
 * @param {Object} entities entities object in the SDK Redux store
 * @param {Array<{ id, type }} resources array of objects
 * with id and type
 * @param {Boolean} throwIfNotFound wheather to skip a resource that
 * is not found (false), or to throw an Error (true)
 *
 * @return {Array} the given resource objects denormalised that were
 * found in the entities
 */
export const denormalisedEntities = (entities, resources, throwIfNotFound = true) => {
  const denormalised = resources.map(res => {
    const { id, type } = res;
    const entityFound = entities[type] && id && entities[type][id.uuid];
    if (!entityFound) {
      if (throwIfNotFound) {
        throw new Error(`Entity with type "${type}" and id "${id ? id.uuid : id}" not found`);
      }
      return null;
    }
    const entity = entities[type][id.uuid];
    const { relationships, ...entityData } = entity;

    if (relationships) {
      // Recursively join in all the relationship entities
      return reduce(
        relationships,
        (ent, relRef, relName) => {
          // A relationship reference can be either a single object or
          // an array of objects. We want to keep that form in the final
          // result.
          const hasMultipleRefs = Array.isArray(relRef.data);
          const multipleRefsEmpty = hasMultipleRefs && relRef.data.length === 0;
          if (!relRef.data || multipleRefsEmpty) {
            ent[relName] = hasMultipleRefs ? [] : null;
          } else {
            const refs = hasMultipleRefs ? relRef.data : [relRef.data];

            // If a relationship is not found, an Error should be thrown
            const rels = denormalisedEntities(entities, refs, true);

            ent[relName] = hasMultipleRefs ? rels : rels[0];
          }
          return ent;
        },
        entityData
      );
    }
    return entityData;
  });
  return denormalised.filter(e => !!e);
};

/**
 * Denormalise the data from the given SDK response
 *
 * @param {Object} sdkResponse response object from an SDK call
 *
 * @return {Array} entities in the response with relationships
 * denormalised from the included data
 */
export const denormalisedResponseEntities = sdkResponse => {
  const apiResponse = sdkResponse.data;
  const data = apiResponse.data;
  const resources = Array.isArray(data) ? data : [data];

  if (!data || resources.length === 0) {
    return [];
  }

  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources);
};

/**
 * Denormalize JSON object.
 * NOTE: Currently, this only handles denormalization of image references
 *
 * @param {JSON} data from Asset API (e.g. page asset)
 * @param {JSON} included array of asset references (currently only images supported)
 * @returns deep copy of data with images denormalized into it.
 */
const denormalizeJsonData = (data, included) => {
  let copy;

  // Handle strings, numbers, booleans, null
  if (data === null || typeof data !== 'object') {
    return data;
  }

  // At this point the data has typeof 'object' (aka Array or Object)
  // Array is the more specific case (of Object)
  if (data instanceof Array) {
    copy = data.map(datum => denormalizeJsonData(datum, included));
    return copy;
  }

  // Generic Objects
  if (data instanceof Object) {
    copy = {};
    Object.entries(data).forEach(([key, value]) => {
      // Handle denormalization of image reference
      const hasImageRefAsValue =
        typeof value == 'object' && value?._ref?.type === 'imageAsset' && value?._ref?.id;
      // If there is no image included,
      // the _ref might contain parameters for image resolver (Asset Delivery API resolves image URLs on the fly)
      const hasUnresolvedImageRef = typeof value == 'object' && value?._ref?.resolver === 'image';

      if (hasImageRefAsValue) {
        const foundRef = included.find(inc => inc.id === value._ref?.id);
        copy[key] = foundRef;
      } else if (hasUnresolvedImageRef) {
        // Don't add faulty image ref
        // Note: At the time of writing, assets can expose resolver configs,
        //       which we don't want to deal with.
      } else {
        copy[key] = denormalizeJsonData(value, included);
      }
    });
    return copy;
  }

  throw new Error("Unable to traverse data! It's not JSON.");
};

/**
 * Denormalize asset json from Asset API.
 * @param {JSON} assetJson in format: { data, included }
 * @returns deep copy of asset data with images denormalized into it.
 */
export const denormalizeAssetData = assetJson => {
  const { data, included } = assetJson || {};
  return denormalizeJsonData(data, included);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} transaction entity object, which is to be ensured against null values
 */
export const ensureTransaction = (transaction, booking = null, listing = null, provider = null) => {
  const empty = {
    id: null,
    type: 'transaction',
    attributes: {},
    booking,
    listing,
    provider,
  };
  return { ...empty, ...transaction };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} booking entity object, which is to be ensured against null values
 */
export const ensureBooking = booking => {
  const empty = { id: null, type: 'booking', attributes: {} };
  return { ...empty, ...booking };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureListing = listing => {
  const empty = {
    id: null,
    type: 'listing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureOwnListing = listing => {
  const empty = {
    id: null,
    type: 'ownListing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} user entity object, which is to be ensured against null values
 */
export const ensureUser = user => {
  const empty = { id: null, type: 'user', attributes: { profile: {} } };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} current user entity object, which is to be ensured against null values
 */
export const ensureCurrentUser = user => {
  const empty = { id: null, type: 'currentUser', attributes: { profile: {} }, profileImage: {} };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} time slot entity object, which is to be ensured against null values
 */
export const ensureTimeSlot = timeSlot => {
  const empty = { id: null, type: 'timeSlot', attributes: {} };
  return { ...empty, ...timeSlot };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureDayAvailabilityPlan = availabilityPlan => {
  const empty = { type: 'availability-plan/day', entries: [] };
  return { ...empty, ...availabilityPlan };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureAvailabilityException = availabilityException => {
  const empty = { id: null, type: 'availabilityException', attributes: {} };
  return { ...empty, ...availabilityException };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensureStripeCustomer = stripeCustomer => {
  const empty = { id: null, type: 'stripeCustomer', attributes: {} };
  return { ...empty, ...stripeCustomer };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensurePaymentMethodCard = stripePaymentMethod => {
  const empty = {
    id: null,
    type: 'stripePaymentMethod',
    attributes: { type: 'stripe-payment-method/card', card: {} },
  };
  const cardPaymentMethod = { ...empty, ...stripePaymentMethod };

  if (cardPaymentMethod.attributes.type !== 'stripe-payment-method/card') {
    throw new Error(`'ensurePaymentMethodCard' got payment method with wrong type.
      'stripe-payment-method/card' was expected, received ${cardPaymentMethod.attributes.type}`);
  }

  return cardPaymentMethod;
};

/**
 * Get the display name of the given user as string. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned or deleted users, a translated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayNameAsString = (user, defaultUserDisplayName) => {
  const hasDisplayName = user?.attributes?.profile?.displayName;

  if (hasDisplayName) {
    return user.attributes.profile.displayName;
  } else {
    return defaultUserDisplayName || '';
  }
};

/**
 * DEPRECATED: Use userDisplayNameAsString function or UserDisplayName component instead
 *
 * @param {propTypes.user} user
 * @param {String} bannedUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayName = (user, bannedUserDisplayName) => {
  console.warn(
    `Function userDisplayName is deprecated!
User function userDisplayNameAsString or component UserDisplayName instead.`
  );

  return userDisplayNameAsString(user, bannedUserDisplayName);
};

/**
 * Get the abbreviated name of the given user. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned  or deleted users, a default abbreviated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserAbbreviatedName
 *
 * @return {String} abbreviated name that can be rendered in the UI
 * (e.g. in Avatar initials)
 */
export const userAbbreviatedName = (user, defaultUserAbbreviatedName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.abbreviatedName;

  if (hasDisplayName) {
    return user.attributes.profile.abbreviatedName;
  } else {
    return defaultUserAbbreviatedName || '';
  }
};

/**
 * A customizer function to be used with the
 * mergeWith function from lodash.
 *
 * Works like merge in every way exept that on case of
 * an array the old value is completely overridden with
 * the new value.
 *
 * @param {Object} objValue Value of current field, denoted by key
 * @param {Object} srcValue New value
 * @param {String} key Key of the field currently being merged
 * @param {Object} object Target object that is receiving values from source
 * @param {Object} source Source object that is merged into object param
 * @param {Object} stack Tracks merged values
 *
 * @return {Object} New value for objValue if the original is an array,
 * otherwise undefined is returned, which results in mergeWith using the
 * standard merging function
 */
export const overrideArrays = (objValue, srcValue, key, object, source, stack) => {
  if (isArray(objValue)) {
    return srcValue;
  }
};

/**
 * Humanizes a line item code. Strips the "line-item/" namespace
 * definition from the beginnign, replaces dashes with spaces and
 * capitalizes the first character.
 *
 * @param {string} code a line item code
 *
 * @return {string} returns the line item code humanized
 */
export const humanizeLineItemCode = code => {
  if (!/^line-item\/.+/.test(code)) {
    throw new Error(`Invalid line item code: ${code}`);
  }
  const lowercase = code.replace(/^line-item\//, '').replace(/-/g, ' ');

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};

export const submitDetailPanelData = (values) => {
  const { boothType, ...rest } = values;
  const boothTypeChilds = {};

  if (Array.isArray(boothType)) {
    boothType.forEach(bt => {
      const keys = [
        "boothTypeOther",
        bt,
        (bt + "Quantity"),
        (bt + "Attendant"),
        (bt + "Other"),
        (bt + "Equipment"),
        (bt + "Other" + "Equipment"),
        (bt + "backdropPreference"),
        (bt + "firstBackdropStyle"),
        (bt + "secondBackdropStyle"),
      ];

      for (const key in rest) {
        if (Object.prototype.hasOwnProperty.call(rest, key)) {
          const element = rest[key];
          if (keys.includes(key) && element) {
            boothTypeChilds[key] = element;
          } else if ([(bt + "Quantity"), (bt + "Attendant")].includes(key)) {
            boothTypeChilds[key] = element || 0;
          }
        }
      }
    });
  }

  return { boothType, boothTypeChilds }
};


export const organizeBoothDetails = (values, boothTypess = []) => {
  const { boothType, ...rest } = values;

  if (!Array.isArray(boothType)) {
    return {};
  }

  const boothDetails = {};

  // Create dynamic booth category mappings from boothTypess
  const boothCategories = {};
  boothTypess.forEach(boothType => {
    // Use parentId to determine the category
    const parentCategory = boothType.parent;
    if (parentCategory) {
      boothCategories[boothType.key] = parentCategory;
    }
  });


  // Fallback mappings for any booth types not in the dynamic mapping
  const fallbackMappings = {
    'ipad': 'photoBooths',
    'ipadCamera': 'photoBooths',
    'pcCamera': 'photoBooths',
    'roamingBooths': 'roamingBooths',
    'roamingPhotography': 'roamingBooths',
    '360Video': 'videoBooths',
    'dj_services': 'specialtyAreas',
    'specialty_services': 'specialtyAreas'
  };

  boothType.forEach(boothTypeId => {
    const category = boothCategories[boothTypeId] || fallbackMappings[boothTypeId] || 'other';

    // Initialize category if not exists
    if (!boothDetails[category]) {
      boothDetails[category] = {};
    }

    // Get quantity
    const quantity = rest[`${boothTypeId}Quantity`] || 0;

    // Get selected subcategories
    const subCategories = rest[boothTypeId] || [];

    // Get equipment
    const equipment = rest[`${boothTypeId}Equipment`] || [];

    // Get other text fields
    const otherField = rest[`${boothTypeId}Other`] || '';

    // Build the booth object
    const boothData = {
      quantity,
      subCategories,
      equipment
    };

    // Add other field if it exists
    if (otherField) {
      boothData.other = otherField;
    }

    // Add to category
    boothDetails[category][boothTypeId] = boothData;
  });

  return boothDetails;
};


// to get initialValues from nested boothDetails structure
export const flattenBoothDetails = (boothDetails) => {
  if (!boothDetails || typeof boothDetails !== 'object') {
    return {};
  }

  const flattenedValues = {};
  const boothTypes = [];

  // Iterate through each category
  Object.values(boothDetails).forEach(category => {
    if (typeof category === 'object') {
      // Iterate through each booth type in the category
      Object.entries(category).forEach(([boothTypeId, boothData]) => {
        if (typeof boothData === 'object') {
          // Add to boothTypes array
          boothTypes.push(boothTypeId);

          // Add quantity
          if (boothData.quantity !== undefined) {
            flattenedValues[`${boothTypeId}Quantity`] = boothData.quantity;
          }

          // Add subcategories
          if (Array.isArray(boothData.subCategories)) {
            flattenedValues[boothTypeId] = boothData.subCategories;
          }

          // Add equipment
          if (Array.isArray(boothData.equipment)) {
            flattenedValues[`${boothTypeId}Equipment`] = boothData.equipment;
          }

          // Add other field if exists
          if (boothData.other) {
            flattenedValues[`${boothTypeId}Other`] = boothData.other;
          }
        }
      });
    }
  });

  // Add boothType array
  if (boothTypes.length > 0) {
    flattenedValues.boothType = boothTypes;
  }

  return flattenedValues;
};

export const allSubCategoriesMakeFilters = (values) => {

  const {
    ipad = [],
    ipadEquipment = [],
    ipadCamera = [],
    ipadCameraEquipment = [],
    pcCamera = [],
    pcCameraEquipment = [],
    roamingBooths = [],
    roamingBoothsEquipment = [],
    roamingPhotography = [],
    roamingPhotographyEquipment = [],
  } = values;

  return {
    ipad,
    ipadEquipment,
    ipadCamera,
    ipadCameraEquipment,
    pcCamera,
    pcCameraEquipment,
    roamingBooths,
    roamingBoothsEquipment,
    roamingPhotography,
    roamingPhotographyEquipment,
    "360Video": values["360Video"] || [],
    "360VideoEquipment": values["360VideoEquipment"] || [],
  }
};

export const submitBoothDetailPanelData = (values) => {
  const { boothType, ...rest } = values;
  const boothTypeChilds = { ...rest };

  boothType.map(bt => {
    const keys = [
      "boothTypeOther",
      bt,
      (bt + "Quantity"),
      (bt + "Attendant"),
      (bt + "Other"),
      (bt + "Equipment"),
      (bt + "Other" + "Equipment"),
      (bt + "backdropPreference"),
      (bt + "firstBackdropStyle"),
      (bt + "secondBackdropStyle"),
    ];

    for (const key in rest) {
      if (Object.prototype.hasOwnProperty.call(rest, key)) {
        const element = rest[key];
        if (keys.includes(key) && element) {
          boothTypeChilds[key] = element;
        } else if ([(bt + "Quantity"), (bt + "Attendant")].includes(key)) {
          boothTypeChilds[key] = 0;
        }
      }
    }
  });

  return { boothType, boothTypeChilds }
};

export const getDetailPanelData = (publicData) => {
  const { boothType, boothTypeChilds } = publicData;
  const getDetailPanelDataValues = {};

  if (boothType) {
    getDetailPanelDataValues["boothType"] = boothType;
  }

  if (boothTypeChilds && typeof boothTypeChilds == "object") {
    for (const key in boothTypeChilds) {
      getDetailPanelDataValues[key] = boothTypeChilds[key];
    }
  }

  return getDetailPanelDataValues;
};

export const getBusinessListingParams = (currentUser) => {
  /**
   * http://localhost:3000/l/:slug/:id/:type/:tab/:listingType >> Prototype
   * http://localhost:3000/l/draft/00000000-0000-0000-0000-000000000000/new/event-details/business >> New
   * http://localhost:3000/l/test-company/65cc9ad7-b0c4-4a0e-9181-8342eebe60a7/draft/details/business >> Draft
   * http://localhost:3000/l/test-company/65cc9125-0c41-4f14-a0ef-a45b919d5220/edit/details/business  >> Published
   */

  const {
    publicData,
    protectedData
  } = (currentUser && currentUser.id && currentUser.attributes.profile) || {};
  const {
    businessListingId = false,
    businessListingPublished = false
  } = protectedData || {};
  const { companyName = "business-company" } = publicData || {};

  const slug = businessListingId ? createSlug(companyName) : 'draft';
  const type = (businessListingPublished && businessListingPublished == BUSINESS_LISTING_TYPE_PUBLISHED)
    ? 'edit'
    : businessListingId
      ? 'draft'
      : 'new';

  return {
    slug, type,
    id: businessListingId || "00000000-0000-0000-0000-000000000000",
    tab: "details",
    listingType: "business"
  };
};

/**
 * Normalise a bid amount to an integer number of minor units, or null.
 *
 * `finalBid.bidAmount` has historically been stored as a *string*: the bid form
 * kept it in React state for a controlled input and wrote it through unchanged.
 * Every read site then tested it with `Number.isFinite`, which is false for a
 * string, so the amount fell back to 0 or null and the organiser saw "$0.00" or
 * a blank cell for a real bid.
 *
 * New bids store an integer. This exists because every bid already in the
 * database holds a string, so all four read sites must accept both.
 *
 * @param {Number|String} value amount in minor units
 *
 * @return {Number|null} the integer amount, or null when unusable
 */
export const toBidAmountCents = value => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
  }
  return null;
};

/**
 * Total requested budget across an RFP's services, formatted for display.
 *
 * The booth details panel stores a budget per selected service, as
 * publicData[serviceKey].budget = { amount, currency }, with amount in minor
 * units. Older RFPs instead carry a publicData.budgets array of
 * { key, amount, currency }, so both shapes are read.
 *
 * Returns '' when no service carries a budget, which drops the row from the
 * notification email rather than showing a figure the organiser never entered.
 */
export const getRfpBudgetLabel = publicData => {
  const { boothTypes, budgets } = publicData || {};
  const serviceKeys = Array.isArray(boothTypes) ? boothTypes : [];

  const entries = [];

  serviceKeys.forEach(key => {
    const budget = publicData?.[key]?.budget;
    if (budget && typeof budget.amount === 'number') {
      entries.push(budget);
    }
  });

  // Fall back to the legacy array for any service the objects did not cover.
  if (!entries.length && Array.isArray(budgets)) {
    budgets.forEach(b => {
      if (b && typeof b.amount === 'number') entries.push(b);
    });
  }

  if (!entries.length) return '';

  const total = entries.reduce((sum, b) => sum + b.amount, 0);
  const currency = entries[0].currency || 'USD';

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      // Sharetribe Money amounts are minor units.
    }).format(total / 100);
  } catch (e) {
    return `${(total / 100).toFixed(0)} ${currency}`;
  }
};

// RFPs moved from a single publicData.eventDate to a publicData.eventDates
// array of "MMM DD,YYYY" strings, and the wizard now writes eventDate: null on
// every save. Reading eventDate alone therefore produced a dateless match
// email for every RFP created with the current form, so build the display
// string from eventDates and only fall back to the legacy single field.
const EVENT_DATE_INPUT_FORMAT = 'MMM DD,YYYY';

export const getEventDateLabel = (publicData = {}) => {
  const { eventDate, eventDates, eventDetails } = publicData;

  const rawDates = Array.isArray(eventDates) && eventDates.length
    ? eventDates
    : Array.isArray(eventDetails)
      ? eventDetails.map(d => d && d.date)
      : [];

  const parsed = rawDates
    .filter(Boolean)
    .map(d => moment(d, EVENT_DATE_INPUT_FORMAT))
    .filter(m => m.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (parsed.length === 1) {
    return parsed[0].format('dddd D MMMM YYYY');
  }
  if (parsed.length > 1) {
    return parsed.map(m => m.format('D MMM YYYY')).join(', ');
  }

  // Legacy RFPs stored a single date, either as a string or as { date }.
  const legacy = eventDate && typeof eventDate === 'object' ? eventDate.date : eventDate;
  if (!legacy) {
    return '';
  }
  const legacyMoment = moment(legacy);
  return legacyMoment.isValid() ? legacyMoment.format('dddd D MMMM YYYY') : String(legacy);
};

/**
 * Turn a category slug into something readable, for when no category record
 * matches it. "360-booth" becomes "360 Booth".
 *
 * @param {string} slug a category slug
 *
 * @return {String} a title-cased label
 */
const humanizeSlug = slug =>
  String(slug || '')
    .split(/[-_]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Display label for one service category slug.
 *
 * RFP publicData and the category records do not always agree on pluralisation:
 * live RFPs carry "360-booth" while the category record is "360-booths". An
 * exact-match-only lookup fell through to the raw slug, so providers saw
 * "360-booth" in the match email. Try the exact slug, then the same slug with a
 * trailing "s" added or removed, and only then fall back to a humanised form of
 * the slug rather than the slug itself.
 *
 * @param {string} slug a category slug from publicData.boothTypes
 * @param {Array} categories category records, each { value, label } and
 *   sometimes { id } when passed straight from the database
 *
 * @return {String} the record's label, or a readable fallback
 */
export const getServiceLabel = (slug, categories) => {
  if (!slug) {
    return '';
  }
  if (!Array.isArray(categories) || categories.length === 0) {
    return humanizeSlug(slug);
  }

  // The /fetch-new-categories endpoint projects only _id, label and value, with
  // no id field, so match value first and keep id for callers that pass
  // category records straight from the database.
  const matches = candidate =>
    categories.find(c => c && (c.value === candidate || c.id === candidate));

  const withoutS = slug.endsWith('s') ? slug.slice(0, -1) : null;
  const candidates = [slug, `${slug}s`, withoutS].filter(Boolean);

  for (const candidate of candidates) {
    const record = matches(candidate);
    if (record && record.label) {
      return record.label;
    }
  }

  return humanizeSlug(slug);
};

export const sendEventMailData = (listing, currentUser, categories) => {
  const { title = "", geolocation, publicData = {} } = listing.attributes;
  // Provide default empty array for boothType to prevent undefined errors
  const { boothType = [], boothTypeChilds, venueAddress, eventTitle } = publicData;
  const { firstName, lastName } = currentUser.attributes.profile;
  const { lat = 40.748466, lng = -73.98554 } = geolocation || {};
  const boothTypess = categories && categories.length
    ? [...categories.filter(d => d.type == 'boothType' && (['all', 'signup'].includes(d.categoryFor))).map(d => ({ key: d.id, label: d.title, parent: d.parentId })).sort((a, b) => a.key.localeCompare(b.key)), { key: "other", label: "Other - Please describe", parent: "other" }]
    : boothTypes;
  console.log('boothType :>> ', boothType);
  // Ensure boothType is an array before filtering
  const filteredBoothType = Array.isArray(boothType)
    ? boothTypess.filter(bt => boothType.includes(bt.key))
    : [];

  const { biddingDeadline } = publicData;

  // Current-model services. RFPs store category slugs in publicData.boothTypes
  // and the chosen subcategories under publicData[slug].subCategories. The
  // legacy boothType/boothTypeChilds fields above are kept for older RFPs, but
  // these are what recipient selection and the email body now use.
  const serviceCategories = Array.isArray(publicData.boothTypes) ? publicData.boothTypes : [];
  const serviceSubcategories = serviceCategories
    .flatMap(slug => publicData?.[slug]?.subCategories)
    .filter(Boolean);
  // Labels for display, resolved from the category records so the email shows
  // "360 Booths" rather than "360-booths".
  const serviceLabels = serviceCategories
    .map(slug => getServiceLabel(slug, categories))
    .filter(Boolean);

  return {
    "_geoloc": {
      lat,
      lng
    },
    boothType: filteredBoothType,
    boothTypeChilds,
    customerName: `${firstName} ${lastName}`,
    customerEmail: currentUser.attributes.email,
    rfpBudget: getRfpBudgetLabel(publicData),
    eventDate: getEventDateLabel(publicData),
    venueName: eventTitle,
    venueAddress: venueAddress?.search || "",
    // The provider needs the RFP's detail page, where they can read it and
    // bid. /openInbox/open/<id> is the provider's own inbox route, which is not
    // where this link should land them.
    eventUrl: eventDetailsUrl(listing),
    // Used by the Resend notification: rfpListingId keys the idempotency guard
    // so a republish cannot mail the same provider twice about one RFP, and the
    // deadline is shown to help providers decide whether it is worth bidding.
    rfpListingId: listing.id.uuid,
    biddingDeadline: biddingDeadline
      ? moment(biddingDeadline).format('dddd D MMMM YYYY, h:mma')
      : "",
    serviceCategories,
    serviceSubcategories,
    serviceLabels
  };
}

export const makeBidData = (eventListing, businessListing) => {
  const { title, publicData } = eventListing.attributes;
  const { title: businessTitle, publicData: businessPublicData } = businessListing.attributes;
  return {
    eventListingData: { title, publicData },
    businessListingData: { title: businessTitle, publicData: businessPublicData },
    eventStartDateUnix: publicData.eventStartDateUnix,
    eventEndDateUnix: publicData.eventEndDateUnix
  }
};

export const getCommisionPrices = (commission, currentUser, response) => {
  const { providerCommission, customerCommission } = (Array.isArray(commission?.data) && commission?.data.length) ? commission.data[0].attributes.data.providerCommission : {};
  const providerPercentageCommission = providerCommission?.percentage || 0;
  const customerPercentageCommission = customerCommission?.percentage || 0;

  const { ExpirationDate, ProviderCommission } = currentUser.attributes.profile.privateData;
  const providerCommissionMaybe = ExpirationDate && ProviderCommission && (moment(ExpirationDate, "DD-MM-YYYY").unix() > moment().unix()) ? ProviderCommission : false;
  const customerCommisionMaybe = response.customerCommisionMaybe?.CustomerCommission ? response.customerCommisionMaybe.CustomerCommission : false;

  return { adminCustomerCommisionMaybe: (customerCommisionMaybe || customerPercentageCommission), adminProviderCommissionMaybe: (providerCommissionMaybe || providerPercentageCommission) };
};

// Copy to clipboards
export const copyToClipboard = text => {
  let dummy = document.createElement("textarea");
  document.body.appendChild(dummy);
  dummy.value = text;
  dummy.select();
  document.execCommand("copy");
  document.body.removeChild(dummy);
}

// Get page url
export const getPageURL = () => {
  if (typeof window !== 'undefined') {
    const pageUrl = (process.env.REACT_APP_MARKETPLACE_ROOT_URL) + window.location.pathname + window.location.search;
    return pageUrl;
  }
};

export const addExtraListings = (listings) => {
  if (!(Array.isArray(listings))) {
    return [];
  } else {
    const newLatLngListings = [];
    console.log(listings, '**** **** => listings');
    listings.map((listing) => {
      const { zipCodeRadius = [] } = listing?.author.attributes.profile?.publicData || {};
      Array.isArray(zipCodeRadius) && zipCodeRadius.forEach((zipcode) => {
        const { origin, primaryZip } = zipcode || {};
        const { lat, lng } = origin || {};
        if (lat && lng) {
          const newListing = JSON.parse(JSON.stringify(listing));
          Object.assign(newListing.id, {
            uuid: newListing.id.uuid + '_' + primaryZip
          });
          Object.assign(newListing.attributes, {
            geolocation: new LatLng(lat, lng)
          });
          newLatLngListings.push(newListing);
        }
      })

    })
    console.log(newLatLngListings, '**** **** => newLatLngListings');

    return newLatLngListings;
  }
};

const mapboxAccesstoken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

export const getDistanceOfUser = (params) => {
  const { customerOrigin, id, zipCode, listingOrigin } = params;

  try {
    // driving,  cycling
    const mode = 'driving';
    const url = `https://api.mapbox.com/directions/v5/mapbox/${mode}/${customerOrigin.lng},${customerOrigin.lat};${listingOrigin.lng},${listingOrigin.lat}?access_token=${mapboxAccesstoken}`;
    return axios.get(url)
      .then((data) => {
        if (data.data.routes.length) {
          const distance = data.data.routes[0].distance;
          // Convert distance to kilometers
          const distanceMeter = parseInt(distance);
          const distanceKm = parseInt(distance / 1000);
          const distanceMile = parseInt((distance / 1000) * 0.621371);

          return { id, zipCode, distance: distanceKm, distanceMile, distanceMeter }
        } else {
          return { id, zipCode, distance: 0 }
        }
      })
      .catch(e => {
        console.error(e, '&&& getDistanceOfUser &&& => e');
        return { id, zipCode, distance: 0 }
      });
  } catch (e) {
    console.error(e, '**** getDistanceOfUser **** => e');
    return { id, zipCode, distance: 0 }
  }
};

export const randomString = () => {
  // Create an array of digits from 0 to 9
  const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  // Shuffle the array randomly
  for (let i = digits.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [digits[i], digits[j]] = [digits[j], digits[i]]; // Swap elements
  }

  // Return the first 8 digits as a string
  return digits.slice(0, 8).join('');
};

export const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for mobile devices
  if (/iphone|ipod|android.*mobile/i.test(userAgent)) {
    return "Mobile";
  }

  // Check for tablets
  if (/ipad|android(?!.*mobile)/i.test(userAgent)) {
    return "Tablet";
  }

  // Check for desktop/laptop
  if (/windows|macintosh|linux/i.test(userAgent)) {
    return "Desktop/Laptop";
  }

  return "Unknown";
}

export const getRootBoothCategories = (data) => {
  if (!Array.isArray(data)) return [];

  return data.filter(
    item =>
      item &&
      typeof item === "object" &&
      item.type === "booth" &&
      item.parentId === null
  );
};


// convert api/crudCategories response to categories array
export const getPrimaryCategories = (data) => {
  if (!Array.isArray(data)) return [];

  // 1. Collect root booth category IDs
  const rootBoothIds = new Set();

  for (const item of data) {
    if (
      item &&
      typeof item === "object" &&
      item.type === "booth" &&
      item.parentId === null &&
      typeof item.id === "string"
    ) {
      rootBoothIds.add(item.id);
    }
  }

  // 2. Collect primary categories (children of root booths)
  const seen = new Set();
  const result = [];

  for (const item of data) {
    if (
      item &&
      typeof item === "object" &&
      typeof item.parentId === "string" &&
      rootBoothIds.has(item.parentId) &&
      typeof item.id === "string" &&
      !seen.has(item.id)
    ) {
      seen.add(item.id);
      result.push(item);
    }
  }

  return result;
};


// get subcategories form main category
export const getSubCategories = (data, parentId) => {
  if (!Array.isArray(data) || typeof parentId !== "string") return [];

  return data.filter(
    item =>
      item &&
      typeof item === "object" &&
      item.parentId === parentId
  );
};

// get subcategories for the new category data
export const getSubcategoriesForNewData = (categories, parentCat) => {
  return categories.find(item => item.value === parentCat)?.subCategories || [];
};

export const getCategoryTitleFromId = (categories, categoryId, fallback = 'N/A') => {
  if (!categoryId) {
    return fallback;
  }

  const list = Array.isArray(categories) ? categories : [];
  const match = list.find(c => (c?.id || c?._id) === categoryId);
  return match?.title || categoryId || fallback;
};


// get event type label from the event type key
export const getEventTypeLabel = (eventTypeKey) => {
  const eventType = EventType.find(et => et.key === eventTypeKey);
  return eventType?.label || eventTypeKey || "N/A";
};



/**
 * Sorts agenda items by Date, then Start Time, then End Time.
 * Handles both ISO strings and readable date strings.
 */
export const sortAgendaItems = (items) => {
  if (!Array.isArray(items)) return [];

  return [...items].sort((a, b) => {
    // 1. Parse dates safely
    const timeA = a.serviceDate?.date ? new Date(a.serviceDate.date).getTime() : 0;
    const timeB = b.serviceDate?.date ? new Date(b.serviceDate.date).getTime() : 0;

    if (timeA !== timeB) {
      return timeA - timeB;
    }

    // 2. Compare Start Times (assumes 24h format "HH:mm")
    const startA = a.startTime || "";
    const startB = b.startTime || "";
    if (startA !== startB) {
      return startA.localeCompare(startB);
    }

    // 3. Compare End Times
    const endA = a.endTime || "";
    const endB = b.endTime || "";
    return endA.localeCompare(endB);
  });
};


/**
 * Returns the URL for a specific event type key.
 * @param {string} typeKey - The key to search for.
 * @returns {string} - The image URL or an empty string if not found.
 */
export const getEventTypeUrl = (typeKey) => {
  // Defensive check for empty or invalid keys
  if (!typeKey) return "";

  const match = EventType.find((item) => item.key === typeKey);

  return match?.url || "";
};

/**
 * Extract city and state/region safely
 * Works whether address is at root level or inside selectedPlace
 */
export function extractCityStateFromAddressObject(addressObj) {
  try {
    if (!addressObj || typeof addressObj !== "object") {
      return { city: null, state: null };
    }

    // Support both:
    // 1) addressObj.city
    // 2) addressObj.selectedPlace.city
    const source = addressObj.selectedPlace || addressObj;

    const city =
      source?.city ||
      source?.town ||
      source?.village ||
      null;

    const state =
      source?.region ||
      source?.state ||
      source?.province ||
      null;

    // If found directly → return
    if (city || state) {
      return {
        city: city || null,
        state: state || null,
      };
    }

    // Fallback: Parse from address string
    const fullAddress = source?.address;

    if (typeof fullAddress === "string") {
      const parts = fullAddress.split(",").map(p => p.trim());

      const fallbackCity = parts[1] || null;

      let fallbackState = null;

      if (parts[2]) {
        fallbackState = parts[2]
          .replace(/[A-Z]\d[A-Z]\s?\d[A-Z]\d/, "") // Remove Canadian postal code
          .trim();
      }

      return {
        city: fallbackCity,
        state: fallbackState,
      };
    }

    return { city: null, state: null };

  } catch (error) {
    console.error("extractCityStateFromAddressObject error:", error);
    return { city: null, state: null };
  }
}


/**
 * Check if late accept window has passed
 *
 * @param {number|string} biddingDuration - Duration in days (can be string)
 * @param {string|Date} transactionCreatedAt - Transaction created date
 * @returns {boolean}
 */
export const isLateAccept = (biddingDuration, transactionCreatedAt) => {
  // Safely convert duration → number
  const durationNum = Number(biddingDuration);

  // Validate inputs
  if (
    !biddingDuration ||                // missing / empty
    isNaN(durationNum) ||             // not convertible to number
    durationNum <= 0 ||               // invalid duration
    !transactionCreatedAt
  ) {
    return false;
  }

  const createdAtDate = new Date(transactionCreatedAt);

  // Invalid date safeguard
  if (isNaN(createdAtDate.getTime())) {
    return false;
  }

  const now = new Date();

  // Days → milliseconds
  const durationMs = durationNum * 24 * 60 * 60 * 1000;

  const expiryTime = createdAtDate.getTime() + durationMs;

  return now.getTime() > expiryTime;
};


export const getSubCategoryLabel = (categories, subcategoryValue) => {
  try {
    if (!Array.isArray(categories) || categories.length === 0) {
      return null;
    }

    if (
      typeof subcategoryValue !== "string" ||
      subcategoryValue.trim().length === 0
    ) {
      return null;
    }

    const targetValue = subcategoryValue.trim();

    for (const category of categories) {
      if (!category || !Array.isArray(category.subCategories)) {
        continue;
      }

      for (const subCategory of category.subCategories) {
        if (
          subCategory &&
          typeof subCategory.value === "string" &&
          subCategory.value === targetValue
        ) {
          return typeof subCategory.label === "string"
            ? subCategory.label
            : null;
        }
      }
    }

    return null;
  } catch (error) {
    console.error("getSubCategoryLabel error:", error);
    return null;
  }
}